import { useState, useRef } from "react";
import { Button } from "./ui/button";
import { Camera, Upload, Loader2 } from "lucide-react";
import { resolveUrl } from "@/lib/queryClient";

export function VisionUploader({ onAnalysisComplete }: { onAnalysisComplete: (data: any) => void }) {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsAnalyzing(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("image", file);

      // We call the authenticated endpoint we created
      const response = await fetch(resolveUrl("/api/vision/analyze-equipment"), {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      const data = await response.json();
      onAnalysisComplete(data);
    } catch (err: any) {
      console.error(err);
      setError("Failed to analyze image: " + err.message);
    } finally {
      setIsAnalyzing(false);
      // reset file input
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-6 bg-muted/20">
      <input
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        ref={fileInputRef}
        onChange={handleFileUpload}
      />
      
      <div className="flex gap-4">
        <Button 
          variant="outline" 
          onClick={() => fileInputRef.current?.click()}
          disabled={isAnalyzing}
        >
          {isAnalyzing ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Upload className="mr-2 h-4 w-4" />
          )}
          {isAnalyzing ? "Analyzing..." : "Upload Photo"}
        </Button>
      </div>
      
      {error && <p className="text-red-500 mt-4 text-sm font-medium">{error}</p>}
      
      <p className="text-muted-foreground text-sm mt-4 text-center">
        Take a picture of an equipment nameplate, pump, or motor to instantly auto-fill specifications and determine ECM viability.
      </p>
    </div>
  );
}
